import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { topicsListPath, postDetailPath } from "../../utils/routes";
import Sidebar from "../../Components/Sidebar/Sidebar";
import styles from "./TopicDetail.module.css";
import { del, get, post, upload } from "../../utils/request";
import {
  getCachedPreviewUrl,
  getFilePreviewUrl,
  getFilePreviewUrlList,
} from "../../utils/attachmentPreview";
import FollowButton from "../../Components/FollowButton/FollowButton";
import Login from "../../Components/Login/Login";
import { useToast } from "../../Components/Toast/Toast";
import ModeratorVerifiedBadge from "../../Components/ModeratorVerifiedBadge/ModeratorVerifiedBadge";
import VoteSection from "../../Components/VoteSection/VoteSection";
import {
  normalizeModeratorsFromArticleData,
  createTopicModeratorChecker,
} from "../../utils/topicModerators";
import { t, noAutoTranslateUserTextProps, mergeReaderNameClass } from "../../utils/i18n";
import {
  createPollAndGetVoteId,
  getPollErrorMessage,
  validatePollDraft,
} from "../../utils/poll";

const DEFAULT_SECOND_IMAGE =
  "https://images.pexels.com/photos/4275070/pexels-photo-4275070.jpeg?auto=compress&cs=tinysrgb&w=1200";

const normalizePostListItem = item => {
  const vid = item?.voteId ?? item?.voteID ?? item?.vetoId;
  const normalized = {
    ...item,
    liked: !!(item?.liked ?? item?.isLiked ?? item?.isThumbed),
  };
  if (vid != null && String(vid).trim() !== "") {
    normalized.voteId = vid;
  }
  return normalized;
};

const POST_UPLOAD_DIRECTORY = process.env.REACT_APP_UPLOAD_DIRECTORY || "";
const POST_UPLOAD_STORAGE_CODE = process.env.REACT_APP_UPLOAD_STORAGE_CODE || "";

const firstCharLetter = (text, fallback = "?") => {
  const s = text != null ? String(text).trim() : "";
  if (!s) return fallback;
  const ch = s.charAt(0);
  return ch || fallback;
};

const AvatarWithFallback = ({
  avatarUrl,
  username,
  imgClassName,
  fallbackClassName,
  emptyFallbackChar = "U",
}) => {
  const [broken, setBroken] = useState(false);
  useEffect(() => {
    setBroken(false);
  }, [avatarUrl]);
  const letter = firstCharLetter(username, emptyFallbackChar);
  const url = avatarUrl != null ? String(avatarUrl).trim() : "";
  if (!url || broken) {
    return (
      <span
        className={mergeReaderNameClass(true, fallbackClassName)}
        aria-hidden="true"
        {...noAutoTranslateUserTextProps}
      >
        {letter}
      </span>
    );
  }
  return (
    <img
      className={mergeReaderNameClass(true, imgClassName)}
      src={url}
      alt=""
      data-mg-native-fallback
      onError={() => setBroken(true)}
      {...noAutoTranslateUserTextProps}
    />
  );
};

const HeroCoverImage = ({
  src,
  alt,
  title,
  /** 封面与下方标题重复时，img 用空 alt，避免整页翻译改写 alt/title 属性 */
  decorative,
  imageClassName,
  fallbackWrapClassName,
  fallbackLetterClassName,
}) => {
  const [broken, setBroken] = useState(false);
  useEffect(() => {
    setBroken(false);
  }, [src]);
  const url = src != null ? String(src).trim() : "";
  const letter = firstCharLetter(title, "?");
  const fallbackAria =
    (decorative ? title || alt : alt || title) || "Topic cover";
  const imgAlt = decorative ? "" : alt || title || "";
  if (!url || broken) {
    return (
      <div
        className={fallbackWrapClassName}
        role="img"
        aria-label={fallbackAria}
      >
        <span
          className={[fallbackLetterClassName, "notranslate"]
            .filter(Boolean)
            .join(" ")}
          {...noAutoTranslateUserTextProps}
        >
          {letter}
        </span>
      </div>
    );
  }
  return (
    <img
      className={imageClassName}
      src={url}
      alt={imgAlt}
      data-mg-native-fallback
      onError={() => setBroken(true)}
    />
  );
};

const PostAttachmentImage = ({ src, label }) => {
  const [broken, setBroken] = useState(false);
  useEffect(() => {
    setBroken(false);
  }, [src]);
  const url = src != null ? String(src).trim() : "";
  const letter = firstCharLetter(label, "?");
  if (!url || broken) {
    return (
      <div className={styles.postAttachmentFallback} aria-hidden="true">
        <span className={styles.postAttachmentFallbackLetter}>{letter}</span>
      </div>
    );
  }
  return (
    <img
      className={styles.postAttachmentImg}
      src={url}
      alt=""
      data-mg-native-fallback
      onError={() => setBroken(true)}
    />
  );
};

const CreatePostThumbImage = ({ src, fallbackLabel }) => {
  const [broken, setBroken] = useState(false);
  useEffect(() => {
    setBroken(false);
  }, [src]);
  const url = src != null ? String(src).trim() : "";
  if (!url || broken) {
    return (
      <div className={styles.createPostImageThumbFallback} aria-hidden="true">
        {firstCharLetter(fallbackLabel, "?")}
      </div>
    );
  }
  return (
    <img src={url} alt="" data-mg-native-fallback onError={() => setBroken(true)} />
  );
};

const CreatePostPreviewImage = ({ src, fallbackLabel }) => {
  const [broken, setBroken] = useState(false);
  useEffect(() => {
    setBroken(false);
  }, [src]);
  const url = src != null ? String(src).trim() : "";
  if (!url || broken) {
    return (
      <div
        className={styles.createPostImagePreviewFallback}
        role="img"
        aria-label={t("aria.preview", "Preview")}
      >
        <span className={styles.createPostImagePreviewFallbackLetter}>
          {firstCharLetter(fallbackLabel, "?")}
        </span>
      </div>
    );
  }
  return (
    <img
      src={url}
      alt=""
      className={styles.createPostImagePreviewImg}
      data-mg-native-fallback
      onError={() => setBroken(true)}
    />
  );
};

const TopicDetail = ({
  theme,
  setTheme,
  isLoggedIn,
  handleOpenLogin,
  cartDisplayed,
  handleCloseCart,
  handleLoginSuccess,
}) => {
  const { topicId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();

  const [topic, setTopic] = useState(null);
  const [posts, setPosts] = useState([]);
  const [postsLoading, setPostsLoading] = useState(false);
  const [postsLoadingMore, setPostsLoadingMore] = useState(false);
  const [postsPage, setPostsPage] = useState(1);
  const [postsHasMore, setPostsHasMore] = useState(true);
  const [postTitle, setPostTitle] = useState("");
  const [postContent, setPostContent] = useState("");
  const [posting, setPosting] = useState(false);
  const [showCreatePostModal, setShowCreatePostModal] = useState(false);
  const [postSelectedImages, setPostSelectedImages] = useState([]);
  const [postAttachmentUuid, setPostAttachmentUuid] = useState("");
  const [postPreviewImageIndex, setPostPreviewImageIndex] = useState(-1);
  const [postImagesUploading, setPostImagesUploading] = useState(false);
  const [postAddVote, setPostAddVote] = useState(false);
  const [postVoteTitle, setPostVoteTitle] = useState("");
  const [postVoteChoiceType, setPostVoteChoiceType] = useState("SINGLE");
  const [postVoteMaxSelected, setPostVoteMaxSelected] = useState(2);
  const [postVoteOptions, setPostVoteOptions] = useState(() => ["", ""]);
  const postImageInputRef = useRef(null);
  const [deletingPostId, setDeletingPostId] = useState(null);
  const [deleteConfirmPostId, setDeleteConfirmPostId] = useState(null);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [postLikeLoadingId, setPostLikeLoadingId] = useState(null);
  const [following, setFollowing] = useState(false);
  const [isHeroDescExpanded, setIsHeroDescExpanded] = useState(false);
  const loadMoreRef = useRef(null);
  const postAttachmentFetchRef = useRef(new Set());
  const postAvatarFetchRef = useRef(new Set());
  const moderatorAvatarFetchRef = useRef(new Set());

  useEffect(() => {
    if (!isLoggedIn) {
      setCurrentUserId(null);
      setDeleteConfirmPostId(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await get("/user/getCurrentUserInfo");
        const raw = res?.data ?? res;
        let data = raw;
        if (raw && typeof raw === "object") {
          const inner = raw?.data;
          if (
            inner &&
            typeof inner === "object" &&
            (inner.id !== undefined ||
              inner.userId !== undefined ||
              inner.nickname !== undefined)
          ) {
            data = inner;
          }
        }
        const id = data?.id ?? data?.userId;
        if (!cancelled) {
          setCurrentUserId(id != null && id !== "" ? String(id) : null);
        }
      } catch {
        if (!cancelled) setCurrentUserId(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isLoggedIn]);

  const isOwnPost = useCallback(
    item => {
      if (
        !item ||
        currentUserId == null ||
        String(currentUserId).trim() === ""
      ) {
        return false;
      }
      const postUserId =
        item.userId ?? item.userID ?? item.creatorId ?? item.uid;
      if (postUserId == null || postUserId === "") return false;
      return String(postUserId) === String(currentUserId);
    },
    [currentUserId],
  );

  const isTopicModeratorUser = useMemo(
    () => createTopicModeratorChecker(topic?.moderators),
    [topic?.moderators],
  );

  // 从后端获取话题详情：GET /social/article/{id}
  useEffect(() => {
    if (!topicId) return;

    let cancelled = false;

    const fetchTopic = async () => {
      setFollowing(false);
      try {
        const res = await get(`/social/article/${topicId}`);
        const data = res?.data || {};

        const attachments = Array.isArray(data.attachments)
          ? data.attachments
          : [];
        const attachmentUuid =
          typeof data.attachmentUuid === "string" ? data.attachmentUuid : "";

        const coverFromData = attachments[0]?.previewUrl || "";

        const normalized = {
          id: String(data.id ?? topicId),
          title: data.title || "",
          content: data.content || "",
          games: Array.isArray(data.games) ? data.games : [],
          collections: Array.isArray(data.collections) ? data.collections : [],
          attachments,
          attachmentUuid,
          moderators: normalizeModeratorsFromArticleData(data),
          // 封面图，优先使用后端直接返回的 previewUrl
          cover: coverFromData,
          // 评论总数：来自 /api/social/article/{id} 的 commentCnt
          commentCnt:
            typeof data.commentCnt === "number"
              ? data.commentCnt
              : Number(data.commentCnt || 0),
        };

        if (!cancelled) {
          setTopic(normalized);
          setFollowing(data.isTracking === true);
        }
      } catch (e) {
        console.warn("Failed to fetch topic detail:", e);
        if (!cancelled) {
          setTopic(null);
        }
      }
    };

    fetchTopic();

    return () => {
      cancelled = true;
    };
  }, [topicId]);

  // 使用详情返回的 attachmentUuid 调用 /api/file/preview，补充封面图片
  useEffect(() => {
    if (!topic?.attachmentUuid || topic.cover) return;

    let cancelled = false;

    getFilePreviewUrl(topic.attachmentUuid)
      .then(url => {
        if (!url || cancelled) return;
        setTopic(prev => (prev ? { ...prev, cover: url } : prev));
      })
      .catch(() => {
        // 出错时保留默认图
      });

    return () => {
      cancelled = true;
    };
  }, [topic?.attachmentUuid, topic?.cover]);

  useEffect(() => {
    if (!topic?.moderators?.length) return;
    let cancelled = false;
    topic.moderators.forEach(mod => {
      if (!mod.avatar || mod.avatarUrl) return;
      const reqKey = `${topic.id}:${mod.id}`;
      if (moderatorAvatarFetchRef.current.has(reqKey)) return;
      moderatorAvatarFetchRef.current.add(reqKey);
      getFilePreviewUrl(mod.avatar).then(url => {
        if (!url || cancelled) return;
        setTopic(prev => {
          if (!prev?.moderators) return prev;
          return {
            ...prev,
            moderators: prev.moderators.map(m =>
              m.id === mod.id ? { ...m, avatarUrl: url } : m,
            ),
          };
        });
      });
    });
    return () => {
      cancelled = true;
    };
  }, [topic?.id, topic?.moderators]);

  // 加载帖子列表：POST /social/post/list
  const fetchPosts = async (pageNo = 1, append = false) => {
    if (!topicId) return;
    if (append) {
      if (postsLoadingMore || postsLoading || !postsHasMore) return;
      setPostsLoadingMore(true);
    } else {
      postAttachmentFetchRef.current = new Set();
      postAvatarFetchRef.current = new Set();
      setPostsLoading(true);
    }
    try {
      const rootIdNum = Number(topicId);
      const rootId = Number.isFinite(rootIdNum) ? rootIdNum : 0;
      const pageSize = 20;
      const res = await post("/social/post/list", {
        data: {
          category: "ARTICLE",
          rootId,
          sort: "",
        },
        pageNo,
        pageSize,
        sortingFieldList: [
          {
            field: "",
            order: "",
          },
        ],
      });

      const raw =
        (Array.isArray(res?.data?.data?.data) && res.data.data.data) ||
        (Array.isArray(res?.data?.data) && res.data.data) ||
        [];
      const total =
        Number(res?.data?.data?.total ?? res?.data?.total ?? raw.length) || 0;
      const hasMore = pageNo * pageSize < total;

      setPosts(prev => {
        if (!append) return raw.map(normalizePostListItem);
        const prevIds = new Set(prev.map(item => String(item.id)));
        const uniqueNew = raw.filter(item => !prevIds.has(String(item.id)));
        return [...prev, ...uniqueNew.map(normalizePostListItem)];
      });
      setPostsPage(pageNo);
      setPostsHasMore(hasMore);
    } catch (e) {
      console.warn("Failed to fetch post list:", e);
      if (!append) {
        setPosts([]);
        setPostsPage(1);
        setPostsHasMore(false);
      }
    } finally {
      if (append) {
        setPostsLoadingMore(false);
      } else {
        setPostsLoading(false);
      }
    }
  };

  useEffect(() => {
    setPosts([]);
    setPostsPage(1);
    setPostsHasMore(true);
    postAttachmentFetchRef.current = new Set();
    postAvatarFetchRef.current = new Set();
    moderatorAvatarFetchRef.current = new Set();
    fetchPosts(1, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topicId]);

  useEffect(() => {
    if (!posts.length) return;
    posts.forEach(item => {
      const id = String(item.id);
      if (postAttachmentFetchRef.current.has(id)) return;
      const uuid = item.attachmentUUID ?? item.attachmentUuid ?? "";
      if (!uuid) return;
      postAttachmentFetchRef.current.add(id);
      getFilePreviewUrlList(uuid)
        .then(urls => {
          setPosts(prev =>
            prev.map(p =>
              String(p.id) === id
                ? { ...p, attachmentImageUrls: Array.isArray(urls) ? urls : [] }
                : p,
            ),
          );
        })
        .catch(() => {
          setPosts(prev =>
            prev.map(p =>
              String(p.id) === id ? { ...p, attachmentImageUrls: [] } : p,
            ),
          );
        });
    });
  }, [posts]);

  useEffect(() => {
    if (!posts.length) return;
    posts.forEach(item => {
      const id = String(item.id);
      const key = `avatar-${id}`;
      if (postAvatarFetchRef.current.has(key)) return;
      const raw = item.avatar;
      if (raw == null || raw === "") return;
      postAvatarFetchRef.current.add(key);

      const rawStr = String(raw).trim();
      if (/^https?:\/\//i.test(rawStr) || rawStr.startsWith("data:")) {
        setPosts(prev =>
          prev.map(p =>
            String(p.id) === id ? { ...p, avatarUrl: rawStr } : p,
          ),
        );
        return;
      }

      const cached = getCachedPreviewUrl(rawStr);
      if (cached) {
        setPosts(prev =>
          prev.map(p =>
            String(p.id) === id ? { ...p, avatarUrl: cached } : p,
          ),
        );
        return;
      }

      getFilePreviewUrl(rawStr)
        .then(url => {
          if (!url) return;
          setPosts(prev =>
            prev.map(p =>
              String(p.id) === id ? { ...p, avatarUrl: url } : p,
            ),
          );
        })
        .catch(() => {});
    });
  }, [posts]);

  useEffect(() => {
    if (!loadMoreRef.current) return;
    if (postsLoading || postsLoadingMore || !postsHasMore) return;
    const observer = new IntersectionObserver(
      entries => {
        const first = entries[0];
        if (!first?.isIntersecting) return;
        fetchPosts(postsPage + 1, true);
      },
      { root: null, rootMargin: "200px 0px", threshold: 0.01 },
    );
    observer.observe(loadMoreRef.current);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postsPage, postsHasMore, postsLoading, postsLoadingMore, topicId]);

  const handleBack = () => {
    navigate(topicsListPath());
  };
  const openPostDetail = postId => {
    navigate(postDetailPath(postId));
  };

  const handlePostCardClick = postId => {
    if (!isLoggedIn) {
      handleOpenLogin?.();
      return;
    }
    openPostDetail(postId);
  };

  const handleTogglePostLike = async (e, item) => {
    e.stopPropagation();
    if (!isLoggedIn) {
      handleOpenLogin?.();
      return;
    }
    const id = item?.id;
    if (id == null || id === "") return;
    if (
      postLikeLoadingId != null &&
      String(postLikeLoadingId) === String(id)
    ) {
      return;
    }
    setPostLikeLoadingId(id);
    const liked = !!item.liked;
    try {
      if (liked) {
        const res = await post("/social/thumb/cancel", {
          category: "POST",
          objectId: id,
        });
        if (res && res.data) {
          setPosts(prev =>
            prev.map(p =>
              String(p.id) === String(id)
                ? {
                    ...p,
                    liked: false,
                    likeCnt: Math.max(0, Number(p.likeCnt || 0) - 1),
                  }
                : p,
            ),
          );
        } else {
          toast.error(res?.message || t("topic.error.removeLike", "Failed to remove like"));
        }
      } else {
        const res = await post("/social/thumb/do", {
          category: "POST",
          objectId: id,
        });
        if (res && res.data) {
          setPosts(prev =>
            prev.map(p =>
              String(p.id) === String(id)
                ? {
                    ...p,
                    liked: true,
                    likeCnt: Number(p.likeCnt || 0) + 1,
                  }
                : p,
            ),
          );
        } else {
          toast.error(res?.message || t("topic.error.like", "Failed to like"));
        }
      }
    } catch (err) {
      toast.error(err?.message || t("topic.error.action", "Action failed, please try again"));
    } finally {
      setPostLikeLoadingId(null);
    }
  };

  const handlePostCommentShortcut = (e, postId) => {
    e.stopPropagation();
    if (!isLoggedIn) {
      handleOpenLogin?.();
      return;
    }
    navigate(postDetailPath(postId, { hash: "#post-comments" }));
  };

  const generatePostAttachmentUuid = () => {
    if (
      typeof crypto !== "undefined" &&
      typeof crypto.randomUUID === "function"
    ) {
      return crypto.randomUUID();
    }
    return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  };

  const resetCreatePostVoteFields = () => {
    setPostAddVote(false);
    setPostVoteTitle("");
    setPostVoteChoiceType("SINGLE");
    setPostVoteMaxSelected(2);
    setPostVoteOptions(["", ""]);
  };

  const handleOpenCreatePost = () => {
    if (!isLoggedIn) {
      handleOpenLogin?.();
      return;
    }
    setPostSelectedImages([]);
    setPostAttachmentUuid(generatePostAttachmentUuid());
    setPostPreviewImageIndex(-1);
    resetCreatePostVoteFields();
    setShowCreatePostModal(true);
  };

  const handleCloseCreatePostModal = () => {
    if (posting || postImagesUploading) return;
    setShowCreatePostModal(false);
    setPostSelectedImages([]);
    setPostAttachmentUuid("");
    setPostPreviewImageIndex(-1);
    resetCreatePostVoteFields();
    if (postImageInputRef.current) postImageInputRef.current.value = "";
  };

  const handlePostVoteOptionChange = (index, value) => {
    setPostVoteOptions(prev => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
  };

  const handleAddPostVoteOption = () => {
    setPostVoteOptions(prev =>
      prev.length >= 8 ? prev : [...prev, ""],
    );
  };

  const handleRemovePostVoteOption = index => {
    setPostVoteOptions(prev =>
      prev.length <= 2 ? prev : prev.filter((_, i) => i !== index),
    );
  };

  const handlePickPostImage = () => {
    postImageInputRef.current?.click();
  };

  const handlePostImageSelected = e => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    const remain = Math.max(0, 9 - postSelectedImages.length);
    if (remain === 0) {
      toast.warning(t("topic.warn.maxImages", "You can upload at most 9 images"));
      e.target.value = "";
      return;
    }

    const picked = files.slice(0, remain);
    if (files.length > remain) {
      toast.warning(t("topic.warn.maxImages", "You can upload at most 9 images"));
    }

    const maxSize = 2 * 1024 * 1024;
    const validFiles = picked.filter(file => {
      if (!file.type.startsWith("image/")) return false;
      if (file.size > maxSize) {
        toast.warning(`Image ${file.name} exceeds 2MB and was skipped`);
        return false;
      }
      return true;
    });

    const readAsDataUrl = file =>
      new Promise(resolve => {
        const reader = new FileReader();
        reader.onload = () =>
          resolve(typeof reader.result === "string" ? reader.result : "");
        reader.onerror = () => resolve("");
        reader.readAsDataURL(file);
      });

    const attachmentUUID =
      postAttachmentUuid || generatePostAttachmentUuid();
    if (!postAttachmentUuid) {
      setPostAttachmentUuid(attachmentUUID);
    }

    setPostImagesUploading(true);

    const uploadOne = async file => {
      const formData = new FormData();
      if (POST_UPLOAD_DIRECTORY) {
        formData.append("directory", POST_UPLOAD_DIRECTORY);
      }
      formData.append("file", file);
      formData.append("attachmentUUID", attachmentUUID);
      formData.append("fileName", file.name || "post-image");
      if (POST_UPLOAD_STORAGE_CODE) {
        formData.append("storageCode", POST_UPLOAD_STORAGE_CODE);
      }
      await upload("/file/multipart", formData);
      const localPreview = await readAsDataUrl(file);
      return localPreview;
    };

    Promise.all(validFiles.map(file => uploadOne(file)))
      .then(urls => {
        const nextImages = urls
          .filter(Boolean)
          .map((src, idx) => ({
            id: `${Date.now()}-${idx}-${Math.random().toString(36).slice(2, 8)}`,
            src,
          }));
        if (nextImages.length > 0) {
          setPostSelectedImages(prev =>
            [...prev, ...nextImages].slice(0, 9),
          );
        }
      })
      .catch(err => {
        console.error("Post image upload failed:", err);
        toast.error(err?.message || t("topic.error.imageUpload", "Image upload failed"));
      })
      .finally(() => {
        setPostImagesUploading(false);
      });

    e.target.value = "";
  };

  const handleRemovePostImage = imageId => {
    setPostSelectedImages(prev => prev.filter(item => item.id !== imageId));
  };

  const handlePostPreviewClose = () => setPostPreviewImageIndex(-1);

  const handlePostPreviewPrev = () => {
    if (!postSelectedImages.length) return;
    setPostPreviewImageIndex(prev =>
      prev <= 0 ? postSelectedImages.length - 1 : prev - 1,
    );
  };

  const handlePostPreviewNext = () => {
    if (!postSelectedImages.length) return;
    setPostPreviewImageIndex(prev =>
      prev >= postSelectedImages.length - 1 ? 0 : prev + 1,
    );
  };

  useEffect(() => {
    if (postPreviewImageIndex < 0) return;
    const len = postSelectedImages.length;
    const onKeyDown = ev => {
      if (ev.key === "Escape") {
        setPostPreviewImageIndex(-1);
      } else if (ev.key === "ArrowLeft" && len > 0) {
        setPostPreviewImageIndex(prev =>
          prev <= 0 ? len - 1 : prev - 1,
        );
      } else if (ev.key === "ArrowRight" && len > 0) {
        setPostPreviewImageIndex(prev =>
          prev >= len - 1 ? 0 : prev + 1,
        );
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [postPreviewImageIndex, postSelectedImages]);

  const handleCreatePost = async () => {
    if (!isLoggedIn) {
      handleOpenLogin?.();
      return;
    }
    const title = postTitle.trim();
    const content = postContent.trim();
    const hasImages = postSelectedImages.length > 0;
    if (!title) {
      toast.warning(t("topic.warn.titleRequired", "Please enter a title"));
      return;
    }
    if (!content && !hasImages) {
      toast.warning(
        t("topic.warn.contentOrImage", "Please enter content or add at least one image"),
      );
      return;
    }
    if (postImagesUploading) {
      toast.warning(t("topic.warn.waitImages", "Please wait for images to finish uploading"));
      return;
    }

    const voteValidation = validatePollDraft({
      title: postVoteTitle,
      options: postVoteOptions,
      choiceType: postVoteChoiceType,
      maxSelected: postVoteMaxSelected,
    });
    if (postAddVote) {
      if (!voteValidation.ok) {
        toast.warning(getPollErrorMessage(voteValidation, t));
        return;
      }
    }

    const rootIdNum = Number(topicId);
    const rootId = Number.isFinite(rootIdNum) ? rootIdNum : 0;
    setPosting(true);
    try {
      let voteIdForPost = null;
      if (postAddVote) {
        try {
          voteIdForPost = await createPollAndGetVoteId({
            title: postVoteTitle,
            options: postVoteOptions,
            choiceType: postVoteChoiceType,
            maxSelected: postVoteMaxSelected,
            parentId: rootId,
            parentType: "post",
          });
        } catch (pollError) {
          toast.error(getPollErrorMessage(pollError, t));
          return;
        }
      }

      const postPayload = {
        attachmentUuid: hasImages ? postAttachmentUuid : "",
        category: "ARTICLE",
        content: content || "",
        id: 0,
        rootId,
        title,
      };
      if (voteIdForPost != null) {
        postPayload.voteId = voteIdForPost;
      }

      const res = await post("/social/post", postPayload);
      if (res?.success === true || res?.data === true) {
        toast.success(t("topic.success.postCreated", "Post created successfully"));
        setPostTitle("");
        setPostContent("");
        setPostSelectedImages([]);
        setPostAttachmentUuid("");
        setPostPreviewImageIndex(-1);
        resetCreatePostVoteFields();
        if (postImageInputRef.current) postImageInputRef.current.value = "";
        setShowCreatePostModal(false);
        await fetchPosts(1, false);
      } else {
        toast.error(
          t("topic.error.createPostRetry", "Failed to create post, please try again later"),
        );
      }
    } catch (e) {
      toast.error(
        e?.message || t("topic.error.createPostRetry", "Failed to create post, please try again later"),
      );
    } finally {
      setPosting(false);
    }
  };

  const openDeletePostConfirm = item => {
    if (!isLoggedIn) {
      handleOpenLogin?.();
      return;
    }
    if (!isOwnPost(item)) {
      toast.warning(t("topic.warn.deleteOwnOnly", "You can only delete your own posts"));
      return;
    }
    setDeleteConfirmPostId(item.id);
  };

  const handleCloseDeletePostConfirm = () => {
    if (deletingPostId) return;
    setDeleteConfirmPostId(null);
  };

  const handleConfirmDeletePost = async () => {
    const postId = deleteConfirmPostId;
    if (!postId) return;
    const target = posts.find(p => String(p.id) === String(postId));
    if (!target || !isOwnPost(target)) {
      toast.warning(t("topic.warn.deleteOwnOnly", "You can only delete your own posts"));
      setDeleteConfirmPostId(null);
      return;
    }
    setDeletingPostId(postId);
    try {
      const res = await del(`/social/post/${postId}`);
      if (res?.success === true || res?.data === true) {
        toast.success(t("topic.success.postDeleted", "Post deleted"));
        setDeleteConfirmPostId(null);
        await fetchPosts(1, false);
      } else {
        toast.error(
          t("topic.error.deletePostRetry", "Failed to delete post, please try again later"),
        );
      }
    } catch (e) {
      toast.error(
        e?.message || t("topic.error.deletePostRetry", "Failed to delete post, please try again later"),
      );
    } finally {
      setDeletingPostId(null);
    }
  };

  const formatDateTime = value => {
    if (!value) return "";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    return d.toLocaleString();
  };

  const heroContentText = (topic?.content || "")
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  const heroDescNeedsToggle = heroContentText.length > 120;
  const heroDescText =
    heroDescNeedsToggle && !isHeroDescExpanded
      ? `${heroContentText.slice(0, 120)}...`
      : heroContentText;

  useEffect(() => {
    setIsHeroDescExpanded(false);
  }, [topic?.id]);

  const activeUsersFromPosts = useMemo(() => {
    const map = new Map();
    for (const p of posts) {
      const key = String(p.userId ?? p.username ?? "").trim();
      if (!key) continue;
      const t = p.createTime ? new Date(p.createTime).getTime() : 0;
      const existing = map.get(key);
      if (!existing || t >= existing.lastTime) {
        map.set(key, {
          id: key,
          username: p.username || existing?.username || "Unknown",
          avatarUrl: p.avatarUrl || existing?.avatarUrl || "",
          lastTime: t,
        });
      }
    }
    return Array.from(map.values())
      .sort((a, b) => b.lastTime - a.lastTime)
      .slice(0, 15);
  }, [posts]);

  // 移除 handleSubmitComment 逻辑，由组件处理

  return (
    <div className={styles.page} data-theme={theme}>
      {cartDisplayed ? (
        <Login
          cartDisplayed={cartDisplayed}
          handleCloseCart={handleCloseCart}
          onLoginSuccess={handleLoginSuccess}
        />
      ) : null}

      <div className={styles.container}>
        <Sidebar
          isOpen={false}
          onClose={() => {}}
          theme={theme}
          setTheme={setTheme}
          isLoggedIn={isLoggedIn}
          handleOpenLogin={handleOpenLogin}
        />

        <main className={styles.mainContent}>
          <header className={styles.headerBar}>
            <button className={styles.backBtn} onClick={handleBack}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="15 18 9 12 15 6" />
              </svg>
              <span>{t("topic.back", "Back to Topic")}</span>
            </button>
          </header>

          <div className={styles.scrollArea}>
            {topic ? (
              <div className={styles.scrollAreaLayout}>
                <div className={styles.scrollAreaMain}>
                  <section className={styles.hero}>
                  {topic.cover ||
                  (topic.attachments && topic.attachments[0]?.previewUrl) ? (
                    <HeroCoverImage
                      src={
                        topic.cover ||
                        (topic.attachments && topic.attachments[0]?.previewUrl)
                      }
                      alt={topic.title}
                      title={topic.title}
                      decorative
                      imageClassName={styles.heroImage}
                      fallbackWrapClassName={styles.heroImageFallback}
                      fallbackLetterClassName={styles.heroImageFallbackLetter}
                    />
                  ) : topic.attachmentUuid ? (
                    <div className={styles.heroImageSlot} aria-hidden="true" />
                  ) : (
                    <HeroCoverImage
                      src={DEFAULT_SECOND_IMAGE}
                      alt={topic.title}
                      title={topic.title}
                      decorative
                      imageClassName={styles.heroImage}
                      fallbackWrapClassName={styles.heroImageFallback}
                      fallbackLetterClassName={styles.heroImageFallbackLetter}
                    />
                  )}
                  <div className={styles.heroOverlay} />
                  <div
                    className={`${styles.heroContent} ${
                      isHeroDescExpanded ? styles.heroContentExpanded : ""
                    }`}
                  >
                    <FollowButton
                      className={styles.followBtnPosition}
                      following={following}
                      isLoggedIn={isLoggedIn}
                      onRequireLogin={handleOpenLogin}
                      onClick={async () => {
                        const res = await post("/user/trace/saveOrCancel", {
                          objectId: String(topicId ?? ""),
                          type: 4,
                        });
                        if (res && res.success) setFollowing(prev => !prev);
                      }}
                    />
                    <h1 className={styles.heroTitle}>
                      <span
                        className="notranslate"
                        {...noAutoTranslateUserTextProps}
                      >
                        {topic.title}
                      </span>
                    </h1>
                    {heroContentText && (
                      <p
                        className={`${styles.heroSubtitle} ${
                          heroDescNeedsToggle && isHeroDescExpanded
                            ? styles.heroSubtitleScrollable
                            : ""
                        }`}
                      >
                        <span
                          className="notranslate"
                          {...noAutoTranslateUserTextProps}
                        >
                          {heroDescText}
                        </span>
                        {heroDescNeedsToggle && (
                          <>
                            {" "}
                            <button
                              type="button"
                              className={styles.heroSubtitleToggle}
                              onClick={() =>
                                setIsHeroDescExpanded(prev => !prev)
                              }
                            >
                              {isHeroDescExpanded
                                ? t("topic.hero.collapse", "Collapse")
                                : t("topic.hero.expand", "Expand")}
                            </button>
                          </>
                        )}
                      </p>
                    )}
                    <p className={styles.heroSubtitle}>
                      {topic.commentCnt
                        ? t("topic.hero.replies", "{count} replies · Enter thread discussion", {
                            count: topic.commentCnt,
                          })
                        : t("topic.hero.enterDiscussion", "Enter thread discussion")}
                    </p>
                  </div>
                </section>

                {/* 帖子列表 */}
                <article className={styles.article}>
                  <div className={styles.mainPostHeader}>
                    <span className={styles.floorBadge}>{t("topic.posts.title", "Post List")}</span>
                    <span className={styles.mainPostMeta}>
                      {t("topic.posts.count", "{count} posts", { count: posts.length })}
                    </span>
                  </div>
                  <div className={styles.createPostBox}>
                    <button
                      type="button"
                      className={styles.createPostBtn}
                      onClick={handleOpenCreatePost}
                    >
                      {t("topic.posts.create", "Create Post")}
                    </button>
                  </div>
                  {postsLoading && (
                    <div className={styles.emptyPosts}>
                      {t("topic.posts.loading", "Loading posts...")}
                    </div>
                  )}
                  {!postsLoading && posts.length === 0 && (
                    <div className={styles.emptyPosts}>{t("topic.posts.empty", "No posts yet")}</div>
                  )}
                  {!postsLoading && posts.length > 0 && (
                    <div className={styles.postList}>
                      {posts.map(item => (
                        <div
                          key={item.id}
                          className={styles.postCard}
                          onClick={() => handlePostCardClick(item.id)}
                          role="button"
                          tabIndex={0}
                          onKeyDown={e => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              handlePostCardClick(item.id);
                            }
                          }}
                        >
                          <div className={styles.postCardHeader}>
                            <div className={styles.postAuthor}>
                              <AvatarWithFallback
                                avatarUrl={item.avatarUrl}
                                username={item.username}
                                imgClassName={styles.postAvatar}
                                fallbackClassName={styles.postAvatarFallback}
                                emptyFallbackChar="U"
                              />
                              <div>
                                <div className={styles.postUsername}>
                                  {item.username ? (
                                    <>
                                      <span
                                        className={mergeReaderNameClass(true)}
                                        {...noAutoTranslateUserTextProps}
                                      >
                                        {item.username}
                                      </span>
                                      {isTopicModeratorUser(
                                        item.userId ??
                                          item.userID ??
                                          item.creatorId ??
                                          item.uid,
                                        item.username,
                                        item,
                                      ) ? (
                                        <ModeratorVerifiedBadge />
                                      ) : null}
                                    </>
                                  ) : (
                                    t("topic.posts.unknownUser", "Unknown User")
                                  )}
                                </div>
                                <div className={styles.postDate}>
                                  {formatDateTime(item.createTime)}
                                </div>
                              </div>
                            </div>
                            <div className={styles.postActions}>
                              {isOwnPost(item) ? (
                                <button
                                  type="button"
                                  className={styles.postDeleteBtn}
                                  onClick={e => {
                                    e.stopPropagation();
                                    openDeletePostConfirm(item);
                                  }}
                                  disabled={
                                    (deletingPostId != null &&
                                      String(deletingPostId) ===
                                        String(item.id)) ||
                                    (deleteConfirmPostId != null &&
                                      String(deleteConfirmPostId) ===
                                        String(item.id))
                                  }
                                >
                                  {deletingPostId != null &&
                                  String(deletingPostId) === String(item.id)
                                    ? t("topic.posts.deleting", "Deleting...")
                                    : t("topic.posts.delete", "Delete")}
                                </button>
                              ) : null}
                            </div>
                          </div>
                          {item.title && (
                            <h3 className={styles.postTitle}>
                              <span
                                className="notranslate"
                                {...noAutoTranslateUserTextProps}
                              >
                                {item.title}
                              </span>
                            </h3>
                          )}
                          {(item.content ||
                            (Array.isArray(item.attachmentImageUrls) &&
                              item.attachmentImageUrls.length > 0)) && (
                            <div className={styles.paragraph}>
                              {item.content ? (
                                <div
                                  className="notranslate"
                                  {...noAutoTranslateUserTextProps}
                                  dangerouslySetInnerHTML={{
                                    __html: item.content,
                                  }}
                                />
                              ) : null}
                              {Array.isArray(item.attachmentImageUrls) &&
                              item.attachmentImageUrls.length > 0 ? (
                                <div
                                  className={
                                    item.attachmentImageUrls.length === 1
                                      ? `${styles.postAttachmentGrid} ${styles.postAttachmentGridOne}`
                                      : styles.postAttachmentGrid
                                  }
                                >
                                  {item.attachmentImageUrls.map((url, i) => (
                                    <PostAttachmentImage
                                      key={`${item.id}-att-${i}`}
                                      src={url}
                                      label={
                                        item.title ||
                                        item.username ||
                                        ""
                                      }
                                    />
                                  ))}
                                </div>
                              ) : null}
                            </div>
                          )}
                          {item.voteId != null &&
                          String(item.voteId).trim() !== "" ? (
                            <div
                              className={styles.postCardVote}
                              onClick={e => e.stopPropagation()}
                              role="presentation"
                            >
                              <VoteSection
                                voteId={item.voteId}
                                isLoggedIn={isLoggedIn}
                                onRequireLogin={handleOpenLogin}
                              />
                            </div>
                          ) : null}
                          <div
                            className={styles.postCardQuickBar}
                            onClick={e => e.stopPropagation()}
                            role="group"
                            aria-label={t("topic.aria.quickActions", "Post quick actions")}
                          >
                            <button
                              type="button"
                              className={`${styles.postQuickBtn} ${
                                item.liked ? styles.postQuickBtnLiked : ""
                              }`}
                              disabled={
                                postLikeLoadingId != null &&
                                String(postLikeLoadingId) === String(item.id)
                              }
                              onClick={e => handleTogglePostLike(e, item)}
                              aria-pressed={!!item.liked}
                              aria-label={t("aria.like", "Like")}
                            >
                              <svg
                                width="18"
                                height="18"
                                viewBox="0 0 24 24"
                                fill={item.liked ? "#ef4444" : "none"}
                                stroke={
                                  item.liked ? "#ef4444" : "currentColor"
                                }
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                aria-hidden
                              >
                                <path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3H14z" />
                                <path d="M7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3" />
                              </svg>
                              <span>{Number(item.likeCnt || 0)}</span>
                            </button>
                            <button
                              type="button"
                              className={styles.postQuickBtn}
                              onClick={e =>
                                handlePostCommentShortcut(e, item.id)
                              }
                              aria-label={t("topic.aria.viewComments", "View comments")}
                            >
                              <svg
                                width="18"
                                height="18"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                aria-hidden
                              >
                                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                              </svg>
                              <span>{Number(item.commentCnt || 0)}</span>
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  {!postsLoading && posts.length > 0 && (
                    <div ref={loadMoreRef} className={styles.postListLoadMore}>
                      {postsLoadingMore
                        ? t("topic.posts.loadingMore", "Loading more posts...")
                        : postsHasMore
                          ? t("topic.posts.scrollLoadMore", "Scroll to bottom to load more")
                          : t("topic.posts.noMore", "No more posts")}
                    </div>
                  )}
                </article>
                </div>
                <aside
                  className={styles.topicSideRail}
                  aria-label={t("topic.aria.sidebar", "Topic sidebar")}
                >
                  <section className={styles.sidePanel}>
                    <h2 className={styles.sidePanelTitle}>
                      {t("topic.side.moderator", "Moderator")}
                    </h2>
                    {Array.isArray(topic.moderators) &&
                    topic.moderators.length > 0 ? (
                      <ul className={styles.sideUserList}>
                        {topic.moderators.map(mod => (
                          <li key={mod.id} className={styles.sideUserRow}>
                            <AvatarWithFallback
                              avatarUrl={mod.avatarUrl}
                              username={mod.username}
                              imgClassName={styles.sideUserAvatar}
                              fallbackClassName={styles.sideUserAvatarFallback}
                              emptyFallbackChar="M"
                            />
                            <span className={styles.sideUserNameWithBadge}>
                              <span
                                className={mergeReaderNameClass(
                                  true,
                                  styles.sideUserName,
                                )}
                                {...noAutoTranslateUserTextProps}
                              >
                                {mod.username}
                              </span>
                              <ModeratorVerifiedBadge />
                            </span>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className={styles.sidePanelEmpty}>
                        {t(
                          "topic.side.moderatorEmpty",
                          "No moderator information available",
                        )}
                      </p>
                    )}
                  </section>
                  <section className={styles.sidePanel}>
                    <h2 className={styles.sidePanelTitle}>
                      {t("topic.side.activeUsers", "Active users")}
                    </h2>
                    {activeUsersFromPosts.length > 0 ? (
                      <ul className={styles.sideUserList}>
                        {activeUsersFromPosts.map(u => (
                          <li key={u.id} className={styles.sideUserRow}>
                            <AvatarWithFallback
                              avatarUrl={u.avatarUrl}
                              username={u.username}
                              imgClassName={styles.sideUserAvatar}
                              fallbackClassName={styles.sideUserAvatarFallback}
                              emptyFallbackChar="U"
                            />
                            <span className={styles.sideUserNameWithBadge}>
                              <span
                                className={mergeReaderNameClass(
                                  true,
                                  styles.sideUserName,
                                )}
                                {...noAutoTranslateUserTextProps}
                              >
                                {u.username}
                              </span>
                              {isTopicModeratorUser(u.id, u.username) ? (
                                <ModeratorVerifiedBadge />
                              ) : null}
                            </span>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className={styles.sidePanelEmpty}>
                        {t("topic.side.activeUsersEmpty", "No active users")}
                      </p>
                    )}
                  </section>
                </aside>
              </div>
            ) : null}
          </div>
        </main>
      </div>
      {showCreatePostModal && (
        <div
          className={styles.createPostModalOverlay}
          onClick={handleCloseCreatePostModal}
        >
          <div
            className={styles.createPostModal}
            onClick={e => e.stopPropagation()}
          >
            <div className={styles.createPostModalBody}>
            <div className={styles.createPostModalHeader}>
              {t("topic.modal.createTitle", "Create Post")}
            </div>
            <input
              type="text"
              className={styles.createPostTitleInput}
              placeholder={t("topic.modal.titlePlaceholder", "Please enter post title")}
              value={postTitle}
              onChange={e => setPostTitle(e.target.value)}
              maxLength={100}
            />
            <textarea
              className={styles.createPostContentInput}
              placeholder={t(
                "topic.modal.contentPlaceholder",
                "Please enter post content (optional if you add images)",
              )}
              value={postContent}
              onChange={e => setPostContent(e.target.value)}
              rows={6}
            />
            {postSelectedImages.length > 0 && (
              <div
                className={`${styles.createPostImageGrid} ${
                  postSelectedImages.length === 1
                    ? styles.createPostImageGridOne
                    : styles.createPostImageGridMany
                }`}
              >
                {postSelectedImages.map((img, idx) => (
                  <div
                    key={img.id}
                    className={styles.createPostImageThumb}
                    role="button"
                    tabIndex={0}
                    onClick={() => setPostPreviewImageIndex(idx)}
                    onKeyDown={e => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setPostPreviewImageIndex(idx);
                      }
                    }}
                    aria-label={t("topic.aria.previewImage", "Preview image")}
                  >
                    <CreatePostThumbImage
                      src={img.src}
                      fallbackLabel={postTitle}
                    />
                    <button
                      type="button"
                      className={styles.createPostImageRemove}
                      onClick={ev => {
                        ev.stopPropagation();
                        handleRemovePostImage(img.id);
                      }}
                      aria-label={t("topic.aria.removeImage", "Remove image")}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div className={styles.createPostToolbar}>
              <div className={styles.createPostUploadGroup}>
                <button
                  type="button"
                  className={styles.createPostImagePickBtn}
                  onClick={handlePickPostImage}
                  disabled={posting || postImagesUploading}
                  aria-label={t("topic.aria.addImages", "Add images")}
                  title="Add images"
                >
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                    <circle cx="8.5" cy="8.5" r="1.5" />
                    <path d="M21 15l-5-5L5 21" />
                  </svg>
                </button>
                <span className={styles.createPostUploadHint}>
                  {postImagesUploading
                    ? t("topic.modal.uploading", "Uploading…")
                    : t("topic.modal.addImages", "Add images (max 9, 2MB each)")}
                </span>
                <input
                  ref={postImageInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  className={styles.createPostImageInputHidden}
                  onChange={handlePostImageSelected}
                />
                <label className={styles.createPostPollToggle}>
                  <input
                    type="checkbox"
                    checked={postAddVote}
                    onChange={e => setPostAddVote(e.target.checked)}
                    disabled={posting || postImagesUploading}
                  />
                  <span>{t("topic.modal.addPoll", "Add poll")}</span>
                </label>
              </div>
              {postAddVote ? (
                <div className={styles.createPostVotePanel}>
                  <input
                    type="text"
                    className={styles.createPostVoteTitleInput}
                    placeholder="Poll question"
                    value={postVoteTitle}
                    onChange={e => setPostVoteTitle(e.target.value)}
                    maxLength={200}
                    disabled={posting}
                  />
                  <div className={styles.createPostVoteTypeRow}>
                    <span className={styles.createPostVoteTypeLabel}>
                      {t("topic.poll.choiceType", "Choice type")}
                    </span>
                    <label className={styles.createPostVoteRadio}>
                      <input
                        type="radio"
                        name="postVoteChoiceType"
                        checked={postVoteChoiceType === "SINGLE"}
                        onChange={() => setPostVoteChoiceType("SINGLE")}
                        disabled={posting}
                      />
                      {t("topic.poll.single", "Single")}
                    </label>
                    <label className={styles.createPostVoteRadio}>
                      <input
                        type="radio"
                        name="postVoteChoiceType"
                        checked={postVoteChoiceType === "MULTI"}
                        onChange={() => setPostVoteChoiceType("MULTI")}
                        disabled={posting}
                      />
                      {t("topic.poll.multiple", "Multiple")}
                    </label>
                    {postVoteChoiceType === "MULTI" ? (
                      <label className={styles.createPostVoteMaxRow}>
                        <span>{t("topic.poll.maxChoices", "Max choices")}</span>
                        <input
                          type="number"
                          className={styles.createPostVoteMaxInput}
                          min={2}
                          max={Math.max(2, postVoteOptions.length)}
                          value={postVoteMaxSelected}
                          onChange={e =>
                            setPostVoteMaxSelected(
                              Number(e.target.value) || 2,
                            )
                          }
                          disabled={posting}
                        />
                      </label>
                    ) : null}
                  </div>
                  <div className={styles.createPostVoteOptionsLabel}>
                    {t("topic.poll.optionsLabel", "Options (2–8)")}
                  </div>
                  <ul className={styles.createPostVoteOptionList}>
                    {postVoteOptions.map((opt, idx) => (
                      <li key={idx} className={styles.createPostVoteOptionRow}>
                        <input
                          type="text"
                          className={styles.createPostVoteOptionInput}
                          placeholder={t("topic.poll.optionPlaceholder", "Option {index}", {
                            index: idx + 1,
                          })}
                          value={opt}
                          onChange={e =>
                            handlePostVoteOptionChange(idx, e.target.value)
                          }
                          maxLength={120}
                          disabled={posting}
                        />
                        <button
                          type="button"
                          className={styles.createPostVoteOptionRemove}
                          onClick={() => handleRemovePostVoteOption(idx)}
                          disabled={
                            posting || postVoteOptions.length <= 2
                          }
                          aria-label={t("topic.aria.removeOption", "Remove option")}
                        >
                          ×
                        </button>
                      </li>
                    ))}
                  </ul>
                  <button
                    type="button"
                    className={styles.createPostVoteAddOption}
                    onClick={handleAddPostVoteOption}
                    disabled={posting || postVoteOptions.length >= 8}
                  >
                    {t("topic.poll.addOption", "+ Add option")}
                  </button>
                </div>
              ) : null}
            </div>
            </div>
            {postPreviewImageIndex >= 0 &&
            postPreviewImageIndex < postSelectedImages.length ? (
              <div
                className={styles.createPostImagePreviewOverlay}
                onClick={handlePostPreviewClose}
                role="presentation"
              >
                <div
                  className={styles.createPostImagePreviewContent}
                  onClick={e => e.stopPropagation()}
                  role="presentation"
                >
                  <button
                    type="button"
                    className={`${styles.createPostImagePreviewNav} ${styles.createPostImagePreviewNavLeft}`}
                    onClick={handlePostPreviewPrev}
                    aria-label={t("aria.prevImage", "Previous image")}
                  >
                    ‹
                  </button>
                  <button
                    type="button"
                    className={styles.createPostImagePreviewClose}
                    onClick={handlePostPreviewClose}
                    aria-label={t("aria.closePreview", "Close preview")}
                  >
                    ×
                  </button>
                  <CreatePostPreviewImage
                    src={
                      postSelectedImages[postPreviewImageIndex]?.src || ""
                    }
                    fallbackLabel={postTitle}
                  />
                  <div className={styles.createPostImagePreviewPager}>
                    {postPreviewImageIndex + 1} / {postSelectedImages.length}
                  </div>
                  <button
                    type="button"
                    className={`${styles.createPostImagePreviewNav} ${styles.createPostImagePreviewNavRight}`}
                    onClick={handlePostPreviewNext}
                    aria-label={t("aria.nextImage", "Next image")}
                  >
                    ›
                  </button>
                </div>
              </div>
            ) : null}
            <div className={styles.createPostAction}>
              <button
                type="button"
                className={styles.createPostCancelBtn}
                onClick={handleCloseCreatePostModal}
                disabled={posting || postImagesUploading}
              >
                {t("topic.modal.cancel", "Cancel")}
              </button>
              <button
                type="button"
                className={styles.createPostBtn}
                onClick={handleCreatePost}
                disabled={posting || postImagesUploading}
              >
                {posting
                  ? t("topic.modal.publishing", "Publishing...")
                  : t("topic.modal.publish", "Publish")}
              </button>
            </div>
          </div>
        </div>
      )}
      {deleteConfirmPostId != null && (
        <div
          className={styles.confirmDialogOverlay}
          onClick={handleCloseDeletePostConfirm}
          role="presentation"
        >
          <div
            className={styles.confirmDialog}
            onClick={e => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="delete-post-confirm-title"
          >
            <h2
              id="delete-post-confirm-title"
              className={styles.confirmDialogTitle}
            >
              {t("topic.delete.title", "Delete post")}
            </h2>
            <p className={styles.confirmDialogMessage}>
              {t(
                "topic.delete.confirm",
                "Are you sure you want to delete this post? This cannot be undone.",
              )}
            </p>
            <div className={styles.confirmDialogActions}>
              <button
                type="button"
                className={styles.createPostCancelBtn}
                onClick={handleCloseDeletePostConfirm}
                disabled={!!deletingPostId}
              >
                {t("topic.modal.cancel", "Cancel")}
              </button>
              <button
                type="button"
                className={styles.confirmDialogDangerBtn}
                onClick={handleConfirmDeletePost}
                disabled={!!deletingPostId}
              >
                {deletingPostId
                  ? t("topic.posts.deleting", "Deleting...")
                  : t("topic.posts.delete", "Delete")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TopicDetail;
